// Pure persona builder (no context imports) so it can be unit-tested.
import { Link } from "react-router-dom";

// Same stops, labels, links and progress keys as JourneyRail STATIONS (asserted in tests).
// Course = Quick Check ("prove" artefact); Prove = Advanced Search receipt ("advanced").
export const PERSONA_STOPS = [
  { key: "pick",   label: "Pick",   to: "/beta/course-fit", progress: "pick",     next: "Pick a subject" },
  { key: "place",  label: "Place",  to: "/cities/matcher",  progress: "place",    next: "Match a city" },
  { key: "course", label: "Course", to: "/admission",       progress: "prove",    next: "Check my admission chances" },
  { key: "prove",  label: "Prove",  to: "/advanced",        progress: "advanced", next: "Run an Advanced Search" },
  { key: "shortlist", label: "Shortlist", to: "/shortlist", progress: "apply",   next: "Build my final shortlist" },
  { key: "apply",  label: "Apply",  to: "/applications",       progress: "applications", next: "Start my applications" },
];

// Preferred display order for cities / unis — keep sentences short.
function joinNatural(items = [], max = 3) {
  const arr = items.slice(0, max).map((s) => String(s).trim()).filter(Boolean);
  if (!arr.length) return "";
  if (arr.length === 1) return arr[0];
  return `${arr.slice(0, -1).join(", ")} and ${arr[arr.length - 1]}`;
}

function humaniseSlug(slug = "") {
  return String(slug)
    .split("-")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

const CURRICULUM_LABEL = {
  "a levels":         "A-Levels",
  "a level":          "A-Levels",
  "a-levels":         "A-Levels",
  "a-level":          "A-Levels",
  "ib":               "the IB",
  "btec":             "a BTEC",
  "scottish highers": "Scottish Highers",
  "highers":          "Highers",
  "cbse":             "CBSE",
  "isc":              "the ISC curriculum",
  "ap":               "AP subjects",
  "sat":              "US high-school (with SAT)",
  "act":              "US high-school (with ACT)",
  "atar":             "ATAR",
  "gpa":              "US high-school (GPA)",
  "american diploma": "the American Diploma",
  "american_diploma": "the American Diploma",
};

function prettyCurriculum(raw) {
  if (!raw) return null;
  const key = String(raw).replace(/[_-]/g, " ").toLowerCase().trim();
  return CURRICULUM_LABEL[key] || key.replace(/\b\w/g, (m) => m.toUpperCase());
}

/**
 * Build the persona sentences from the journey profile.
 * Returns a flat list of {key, text} pairs plus a `nextGoal` string —
 * sentences that shouldn't render (no source data) are omitted.
 */
export function buildPersona(profile) {
  const what  = profile?.what  || {};
  const where = profile?.where || {};
  const prove = profile?.prove || {};
  const how   = profile?.how   || {};
  const grades = how.grades || {};
  const confirmedGrades = grades.confirmed || {};

  // Safety net: earlier versions of CourseFit incorrectly stored the
  // RANK label ("Leading match", "Close match", "Also worth exploring")
  // as the confirmed subject's label. Filter those known non-subject
  // strings out so returning users don't see "I am leaning toward
  // studying Also worth exploring."
  const RANK_LABEL_BLOCKLIST = new Set([
    "leading match", "close match", "also worth exploring",
  ]);
  const subjects = (what.subjects_confirmed || []).map((s) =>
    (typeof s === "string" ? s : (s?.label || s?.slug)))
    .filter(Boolean)
    .filter((v) => !RANK_LABEL_BLOCKLIST.has(String(v).toLowerCase().trim()));
  const cities = (where.cities_confirmed || []).map((c) =>
    typeof c === "string" ? humaniseSlug(c) : (c?.name || humaniseSlug(c?.slug || "")));
  const chances = (prove.chances_confirmed || []).map((u) =>
    typeof u === "string" ? u : (u?.label || u?.name || u?.slug)).filter(Boolean);
  // Shortlist entries — always prefer a real name. If we only have a
  // slug like "uk-lipa" (legacy data) we humanise it ("Uk Lipa" →
  // "LIPA") so the sentence stays readable. Slugs with 2-4 letters
  // are almost always acronyms (UCLA, LIPA, LSE, MIT) so we upper-case
  // them; longer ones become Title Case ("Cambridge University").
  const humaniseUniSlug = (slug = "") => {
    const parts = String(slug).split("-").filter(Boolean);
    // Drop a leading country prefix like "uk-" / "us-" / "au-" that
    // students never read out loud.
    const trimmed = parts.length > 1 && parts[0].length === 2 ? parts.slice(1) : parts;
    return trimmed
      .map((w) => (w.length <= 4 ? w.toUpperCase() : (w[0].toUpperCase() + w.slice(1))))
      .join(" ");
  };
  const shortlist = (how.shortlist_confirmed || []).map((u) => {
    if (typeof u === "string") return humaniseUniSlug(u);
    // Prefer explicit name → uni_name → slug-derived
    return u?.name
        || u?.label
        || u?.uni_name
        || humaniseUniSlug(u?.uni_slug || u?.slug || "");
  }).filter(Boolean);

  const sentences = [];

  // I am currently studying …
  const curr = confirmedGrades.curriculum ? prettyCurriculum(grades.curriculum) : null;
  if (curr) {
    sentences.push({ key: "curriculum", text: `I am currently studying ${curr}.` });
  }

  // I am predicted …
  const predicted = confirmedGrades.predicted ? grades.predicted : null;
  if (predicted) {
    sentences.push({ key: "predicted", text: `I am predicted ${predicted}.` });
  }

  // I am leaning toward studying …
  if (subjects.length) {
    sentences.push({
      key: "subjects",
      text: `I am leaning toward studying ${joinNatural(subjects)}.`,
    });
  }

  // I want to study in …
  if (cities.length) {
    sentences.push({
      key: "cities",
      text: `I want to study in ${joinNatural(cities)}.`,
    });
  }

  // I've checked my chances against …
  if (chances.length) {
    sentences.push({
      key: "chances",
      text: `I've checked my chances against ${joinNatural(chances, 3)}${chances.length > 3 ? ` and ${chances.length - 3} more course${chances.length - 3 === 1 ? "" : "s"}` : ""}.`,
    });
  }

  // My final shortlist / plan to apply to …
  if (shortlist.length) {
    const namePart = shortlist.length <= 3
      ? `: ${joinNatural(shortlist, 3)}`
      : ` — starting with ${joinNatural(shortlist, 3)}`;
    sentences.push({
      key: "shortlist",
      text: `My final shortlist is ${shortlist.length} universit${shortlist.length === 1 ? "y" : "ies"}${namePart}.`,
    });
  }

  // Empty state — never leave the card blank.
  if (sentences.length === 0) {
    sentences.push({
      key: "empty",
      text: "I'm just starting to explore my options.",
    });
  }

  // My next goal is …
  const rawDone = new Set(profile?.behaviour?.pillars_completed || []);
  // Apply has no completion artefact yet, so it never blocks "journey complete".
  const PILLAR_ORDER = PERSONA_STOPS.filter((st) => st.progress !== "applications").map((st) => st.progress);
  // If the student has confirmed a LATER pillar, they've clearly
  // walked through the earlier ones — treat those as implicitly
  // complete for messaging purposes. Prevents the confusing "go
  // back to Prove" nag after Apply is done.
  const done = new Set(rawDone);
  const highestDoneIdx = Math.max(
    ...PILLAR_ORDER.map((k, i) => (rawDone.has(k) ? i : -1)),
    -1,
  );
  for (let i = 0; i <= highestDoneIdx; i += 1) done.add(PILLAR_ORDER[i]);

  const firstUndone = PILLAR_ORDER.find((k) => !done.has(k));

  let nextGoal;
  if (!firstUndone && rawDone.has("applications")) {
    nextGoal = "I've exported every application — now I just need to send them off before each deadline!";
  } else if (!firstUndone) {
    // When done we render a small trailing link to the deadlines
    // view, so the sentence renders as JSX (not a plain string).
    nextGoal = (
      <>
        I've confirmed every stage — I know my destination! Now I need to work on my applications!{" "}
        <Link
          to="/shortlist#deadlines"
          data-testid="persona-deadlines-link"
          className="underline decoration-2 underline-offset-2 text-[#4C1D95] hover:text-[#3B0764]"
        >
          Let me find the important deadlines…
        </Link>
      </>
    );
  } else if (firstUndone === "pick") {
    nextGoal = "My next goal is to lock in a subject — the 4-minute Course Fit does it.";
  } else if (firstUndone === "place") {
    nextGoal = "My next goal is to confirm the cities that fit my life and my budget.";
  } else if (firstUndone === "prove") {
    nextGoal = predicted
      ? `My next goal is to see which universities are Likely, Target and Ambitious for someone predicted ${predicted}.`
      : "My next goal is to check my admission chances against my target courses.";
  } else if (firstUndone === "advanced") {
    nextGoal = "My next goal is to run an Advanced Search and see where I really stand.";
  } else {
    nextGoal = "My next goal is to lock in the final 3-5 universities I'll apply to.";
  }

  return { sentences, nextGoal, journeyComplete: !firstUndone, gradesPending: !curr && !predicted };
}


// Wording per curriculum: [noun, isPlural]. Unknown curricula fall back to "predicted grades".
const GRADE_NOUN = {
  a_level: ["predicted grades", true], ib: ["predicted IB points", true], atar: ["predicted ATAR", false],
  cbse: ["expected percentage", false], isc: ["expected percentage", false],
  american_diploma: ["GPA", false], gpa: ["GPA", false], ap_composite: ["AP scores", true],
  abitur: ["expected Abitur grade", false], french_bac: ["expected Bac mark", false],
  hkdse: ["predicted HKDSE grades", true], suneung: ["expected Suneung score", false],
};
export const GRADE_FIELDS = Object.keys(GRADE_NOUN);
const hasValue = (v) => v !== undefined && v !== null && String(v).trim() !== "";

/** Saved curriculum/grade values that were never entered or confirmed by the student
 *  (any value, default-looking or not). Null when there is nothing to ask. */
export function savedGradesToConfirm(profile) {
  const g = profile?.how?.grades || {};
  const c = g.confirmed || {};
  const askCurriculum = hasValue(g.curriculum) && c.curriculum !== true;
  const askPredicted = hasValue(g.predicted) && c.predicted !== true;
  if (!askCurriculum && !askPredicted) return null;
  const curriculum = hasValue(g.curriculum) ? String(g.curriculum) : null;
  const curriculumLabel = curriculum ? prettyCurriculum(curriculum) : null;
  const [noun, plural] = GRADE_NOUN[curriculum] || ["predicted grades", true];
  const question = askPredicted
    ? `${plural ? "Are these" : "Is this"} your current ${noun}?`
    : `Are you currently studying ${curriculumLabel}?`;
  return {
    askCurriculum, askPredicted, curriculum, curriculumLabel,
    curriculumConfirmed: c.curriculum === true,
    predicted: hasValue(g.predicted) ? String(g.predicted) : null,
    noun, question,
  };
}

/** Journey patch confirming exactly the values shown in the prompt (and nothing else). */
export function savedGradesConfirmPatch(profile, item) {
  const g = profile?.how?.grades || {};
  const confirmed = { ...(g.confirmed || {}) };
  if (item.askCurriculum) confirmed.curriculum = true;
  if (item.askPredicted) confirmed.predicted = true;
  const grades = { ...g, confirmed, source: "persona_confirm" };
  if (item.askCurriculum) grades.curriculum = item.curriculum;
  if (item.askPredicted) grades.predicted = item.predicted;
  return { how: { grades } };
}
